/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, OnInit } from '@angular/core';

import { Observable } from 'rxjs/Observable';

import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';

import { AccountService } from '../../services/account.service';
import { ConfigService } from '../../services/config.service';
import { GmailService } from '../../services/gmail.service';
import { O365Service } from '../../services/o365.service';
import { CacheService } from '../../global/ng2-cache/services/cache.service';
import { SessionService } from '../../services/session2.service';
import { GlobalMessageService } from '../../services/global-message.service';
import { DriftService } from '../../services/drift.service';

import { ConfirmDialogComponent } from '../../global/confirm-dialog/confirm-dialog.component';
import { AlertDialogComponent } from '../../global/alert-dialog/alert-dialog.component';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-settings-inboxes',
  templateUrl: './settings-inboxes.component.html'
})
export class SettingsInboxesComponent implements OnInit {

  loadingPage: boolean = true;
  session: any = null;

  inviteDialogRef: BsModalRef;
  editDialogRef: BsModalRef;

  inboxes: any[] = [];
  inboxBeingEdited = null;

  gmailConfig: any = {};
  o365Config: any = {};

  inviteForm: FormGroup;
  editForm: FormGroup;
  emailBlur: boolean = false;

  inboxConnectionVisible = false;

  constructor(private accountService: AccountService,
    private configService: ConfigService,
    private gmailService: GmailService,
    private o365Service: O365Service,
    private cacheService: CacheService,
    private sessionService: SessionService,
    private driftService: DriftService,
    private modalService: BsModalService,
    private fb: FormBuilder,
    private globalMessageService: GlobalMessageService) {
    this.inviteForm = fb.group({
      name: ['', Validators.compose([Validators.required, Validators.minLength(1)])],
      email: ['', Validators.compose([Validators.required, Validators.minLength(1), Validators.pattern('[a-zA-Z0-9+-._]+(\.[_a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,15})')])]
    });
    this.editForm = fb.group({
      name: ['', Validators.compose([Validators.required, Validators.minLength(1)])],
    });
  }

  ngOnInit() {
    this.session = this.cacheService.get('session');
    if (!this.session) {
      this.sessionService.getSession(true).subscribe(
        (session: any) => {
          this.session = session;
          this.initView();
        }
      );
    }
    else {
      this.initView();
    }
  }

  authGmail() {
    let state = encodeURIComponent('{"email":"direct"}');
    var url = `${this.gmailConfig.authUrl}?` +
      `response_type=code&prompt=consent&access_type=offline&` +
      `client_id=${this.gmailConfig.clientId}&` +
      `redirect_uri=${this.gmailConfig.redirectUrl}&` +
      `scope=${this.gmailConfig.scopes}&state=${state}`;

    window.location.href = url;
  }

  authO365() {
    let state = encodeURIComponent('{"email":"direct"}');
    let url = `${this.o365Config.authUrl}?` +
      `response_type=code&response_mode=query&` +
      `client_id=${this.o365Config.clientId}&` +
      `redirect_uri=${this.o365Config.redirectUrl}&` +
      `scope=${encodeURIComponent(this.o365Config.scopes)}&state=${state}`;
    window.location.href = url;
  }

  newInvite(dialog) {
    this.initInviteForm();
    this.inviteDialogRef = this.modalService.show(dialog, { class: 'modal-md' });
  }

  sendInvite() {
    let model = {
      name: this.inviteForm.controls['name'].value,
      email_address: this.inviteForm.controls['email'].value.toLowerCase(),
      type: 'gmail'
    };

    this.accountService.inviteInbox(model).subscribe(
      (result: any) => {
        if (!result) {
          this.modalService.show(AlertDialogComponent, Object.assign({}, {
            initialState: {
              header: `Inbox already connected`,
              message: `An inbox with the email address ${model.email_address} is already connected. If you think this is an error please contact support@drift.com`,
              confirmBtn: 'Ok'
            }
          }));
        }
        else {
          this.driftService.track('invited-inbox', { email: model.email_address });
          this.globalMessageService.message('Invitation sent');
          this.initView();
        }
      },
      error => {

      }
    );
  }

  reInvite(inbox) {
    let me = this;
    this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        header: `Invite this user again?`,
        message: `Please confirm you want to re-invite ${inbox.email_address}. A new invitation will be sent once you confirm.`,
        confirmBtn: 'Send Invite',
        confirm: function () {
          me.accountService.inviteExistingInbox(inbox._id).subscribe(
            (result: any) => {
              me.globalMessageService.message('Invitation sent');
              me.initView();
            },
            error => {
            }
          );
        }
      }
    }));
  }

  delete(inbox) {
    let me = this;
    this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        header: `Delete this connected inbox?`,
        message: `Are you sure you want to delete the inbox sync for ${inbox.email_address}? This will stop Drift Email from processing future replies but past data will remain in your account.`,
        confirmBtn: 'Delete',
        confirm: function () {
          me.accountService.deleteInbox(inbox._id).subscribe(
            (result: any) => {
              me.driftService.track('deleted-inbox', { email: inbox.email_address });
              me.globalMessageService.message('Inbox was deleted');
              me.initView();
            },
            error => {
            }
          );
        }
      }
    }));
  }

  edit(inbox, dialog) {
    let me = this;
    this.inboxBeingEdited = inbox;
    this.editForm.controls['name'].setValue(this.inboxBeingEdited.name);
    this.editForm.markAsPristine();
    this.editDialogRef = this.modalService.show(dialog, Object.assign({}, {}));
  }

  confirmEdit(inbox) {
    let newName = this.editForm.controls['name'].value;
    this.editDialogRef.hide();
    this.accountService.renameInbox(this.inboxBeingEdited._id, newName).subscribe(
      (result: any) => {
        this.initView();
        this.inboxBeingEdited = null;
        this.globalMessageService.message('Updated inbox name');
      },
      (error) => {
        this.globalMessageService.error();
      }
    )

  }

  test(inbox) {
    if (inbox.type == "gmail") {
      this.gmailService.testAccess(inbox._id).subscribe(
        (result: any) => {
          this.modalService.show(AlertDialogComponent, Object.assign({}, {
            initialState: {
              message: `This inbox connection is working.`,
              confirmBtn: 'Close'
            }
          }));
        },
        (error) => {
          const errorMessage = error.error.results.message;
          const displayError = errorMessage.includes('invalid_grant') ? 'Invalid grant - Check settings in Gmail to see that Drift Email has access.' : errorMessage;
          this.modalService.show(AlertDialogComponent, Object.assign({}, {
            initialState: {
              message: `This inbox connection is broken.`,
              confirmBtn: 'Close',
              moreInfoText: `Error: ${displayError}`
            }
          }));
        }
      );
    }
    else {
      this.o365Service.testAccess(inbox._id).subscribe(
        (result: any) => {
          this.modalService.show(AlertDialogComponent, Object.assign({}, {
            initialState: {
              message: `This inbox connection is working.`,
              confirmBtn: 'Close'
            }
          }));
        },
        (error) => {
          const errorMessage = error.error.results.message;
          const displayError = errorMessage.includes('invalid_grant') ? 'Invalid grant - Check settings in your O365 account to see that Drift Email has access.' : errorMessage;
          this.modalService.show(AlertDialogComponent, Object.assign({}, {
            initialState: {
              message: `This inbox connection is broken.`,
              confirmBtn: 'Close',
              moreInfoText: `Error: ${displayError}`
            }
          }));
        }
      )
    }
  }

  /**
   * Special validation for email since we don't want validation until blur, and we want to trim whitespace
   * for copy/paste bugs
   */
  trimEmail() {
    this.inviteForm.controls['email'].setValue(this.inviteForm.controls['email'].value.trim());
    this.emailBlur = true;
  }

  private initView() {
    this.loadingPage = true;
    const session = this.cacheService.get('session');
    if (session.f) {
      this.inboxConnectionVisible = session.f.inviteConnection || false;
    }

    Observable.forkJoin(
      this.accountService.getInboxes(),
      this.configService.gmailConfig(),
      this.configService.o365Config()
    ).subscribe(
      (result: any) => {
        this.inboxes = result[0];
        this.gmailConfig = result[1];
        this.o365Config = result[2];
        this.loadingPage = false;
      },
      error => {

      }
    )
  }

  private initInviteForm() {
    this.inviteForm.controls['name'].setValue('');
    this.inviteForm.controls['email'].setValue('');
    this.inviteForm.markAsPristine();
  }
}



// WEBPACK FOOTER //
// ./client/app/settings/settings-inboxes/settings-inboxes.component.ts